// Globals
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { selectChannel } from '@/modules/Channels';
import { Header } from '@/modules/Layout';
import {
  LLMTemplateContentForm,
  LLMTemplateVariablesList,
  TLLMTemplateFormValues,
  useGetLLMTemplateVariables,
} from '@/modules/LLMTemplateCommon';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { updateChannelLLMTemplate } from '../../feature';

// Components
import { showSuccessToast, useForm } from '@/components';
import { CheckIcon, ResetIcon } from '@/icons';
import { Button, Spinner, Tag } from '@/ui';
import {
  StyledActions,
  StyledContainer,
  StyledPageWrapper,
  StyledSpinnerWrapper,
  StyledVariablesSidebar,
} from './LLMChannelTemplateContainerStyles';
import { LLMChannelTemplateResetModal } from '../../components';

// Helpers
import { useBoolean } from '@/hooks';
import { useGetChannelLLMTemplate } from '../../hooks';
import { getLLMChannelTemplateInitialValues, getLLMChannelTemplateSourceTagProps } from '../../helpers';

const LLMChannelTemplateContainer = () => {
  const { t } = useTranslation();
  const channel = useAppState(selectChannel);
  const dispatch = useAppDispatch();
  const { isLoading, template } = useGetChannelLLMTemplate({ channelId: channel?.id });
  const { isLoading: isVariablesLoading, variables } = useGetLLMTemplateVariables();
  const [form] = useForm();
  const { value: isResetModalOpen, setTrue: handleOpenResetModal, setFalse: handleCloseResetModal } = useBoolean();

  const initialValues = useMemo(() => getLLMChannelTemplateInitialValues(template), [template]);

  useEffect(() => {
    if (template) {
      form.setFieldsValue(getLLMChannelTemplateInitialValues(template));
    }
  }, [template, form]);

  const handleSave = async (values: TLLMTemplateFormValues) => {
    if (!channel?.id) return;

    try {
      await dispatch(updateChannelLLMTemplate({ channelId: channel.id, content: values.content })).unwrap();
      showSuccessToast({ message: t('save_changes') });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveClick = () => {
    form.submit();
  };

  const sourceTagProps = useMemo(() => getLLMChannelTemplateSourceTagProps(template), [template]);

  return (
    <>
      <StyledPageWrapper>
        <Header title={t('menu_llm_channel_template')} suptitle={t('menu_seo')}>
          <StyledActions>
            {!!template && <Tag color={sourceTagProps.color}>{t(sourceTagProps.labelKey)}</Tag>}
            <Button icon={<ResetIcon />} onClick={handleOpenResetModal} disabled={!channel?.id || isLoading}>
              {t('btn_reset')}
            </Button>
            <Button
              icon={<CheckIcon />}
              type="primary"
              loading={isLoading}
              onClick={handleSaveClick}
              disabled={!channel?.id || isLoading}
            >
              {t('btn_save')}
            </Button>
          </StyledActions>
        </Header>
        <StyledSpinnerWrapper>
          <Spinner size="large" spinning={isLoading}>
            <StyledContainer>
              <LLMTemplateContentForm form={form} initialValues={initialValues} onSubmit={handleSave} />
              <StyledVariablesSidebar>
                <LLMTemplateVariablesList variables={variables} isLoading={isVariablesLoading} />
              </StyledVariablesSidebar>
            </StyledContainer>
          </Spinner>
        </StyledSpinnerWrapper>
      </StyledPageWrapper>
      {!!channel?.id && (
        <LLMChannelTemplateResetModal
          channelId={channel.id}
          isOpen={isResetModalOpen}
          onClose={handleCloseResetModal}
        />
      )}
    </>
  );
};

export default LLMChannelTemplateContainer;
